import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import { pdf } from '@react-pdf/renderer';
import ExportPDFFdwRestDayAgreement from './ExportPDFFdwRestDayAgreement';

// eslint-disable-next-line react/prop-types
const FdwRestDayAgreement = ({ booking_id }) => {
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [agencyInfo, setAgencyInfo] = useState({});
  // Fetch existing rest day agreement data
  const fetchRestDayData = async () => {
    if (!booking_id) return;
    
    setIsLoading(true);
    try {
      const response = await api.get(`external-forms/fdw-rest-day-agreements/${booking_id}/`);
      if (response.data) {
        setAgencyInfo({
          agencyAddress: response.data.agency_address || '',
          agencyPhone: response.data.agency_phone || ''
        });
        setFormData({
          fdwName: response.data.fdw_name || '',
          fdwWp: response.data.fdw_wp || '',
          employerName: response.data.employer_name || '',
          employerNric: response.data.employer_ic || '',
          agreement: response.data.agreement || '',
          compAmount: response.data.comp_amount || '',
          fdwSignature: response.data.fdw_signature || '',
          fdwDate: response.data.fdw_date || '',
          employerSignature: response.data.employer_signature || '',
          employerDate: response.data.employer_date || '',
          variation: response.data.variation || '',
          restInLieu: response.data.rest_in_lieu || false,
          restForgone: response.data.rest_forgone || false,
          compensation: response.data.compensation || false,
          restWorked: response.data.rest_worked || '',
          fdwSignatureVar: response.data.fdw_signature_var || '',
          fdwDateVar: response.data.fdw_date_var || '',
          employerSignatureVar: response.data.employer_signature_var || '',
          employerDateVar: response.data.employer_date_var || ''
        });
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching rest day agreement data:', error);
        setErrors({ fetch: 'Failed to load rest day agreement data' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRestDayData();
  }, [booking_id]);
  
  const [formData, setFormData] = useState({
    fdwName: '',
    fdwWp: '',
    employerName: '',
    employerNric: '',
    agreement: '',
    compAmount: '',
    fdwSignature: '',
    fdwDate: '',
    employerSignature: '',
    employerDate: '',
    variation: '',
    restInLieu: '',
    restForgone: '',
    compensation: '',
    restWorked: '',
    fdwSignatureVar: '',
    fdwDateVar: '',
    employerSignatureVar: '',
    employerDateVar: ''
  });

  const [result, setResult] = useState('');

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const submitForm = async () => {
    if (!booking_id) {
      setErrors({ submit: 'Booking ID is required' });
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    
    try {
      const apiData = {
        booking: booking_id,
        fdw_name: formData.fdwName,
        fdw_wp: formData.fdwWp,
        employer_name: formData.employerName,
        employer_nric: formData.employerNric,
        agreement: formData.agreement,
        comp_amount: formData.compAmount,
        fdw_signature: formData.fdwSignature,
        fdw_date: formData.fdwDate || null,
        employer_signature: formData.employerSignature,
        employer_date: formData.employerDate || null,
        variation: formData.variation,
        rest_in_lieu: formData.restInLieu,
        rest_forgone: formData.restForgone,
        compensation: formData.compensation,
        rest_worked: formData.restWorked,
        fdw_signature_var: formData.fdwSignatureVar,
        fdw_date_var: formData.fdwDateVar || null,
        employer_signature_var: formData.employerSignatureVar,
        employer_date_var: formData.employerDateVar || null
      };

      const response = await api.post(`external-forms/fdw-rest-day-agreements/${booking_id}/`, apiData);
      setResult("Form submitted successfully!");
      console.log('Rest day agreement submitted:', response.data);
    } catch (error) {
      console.error('Error submitting rest day agreement:', error);
      if (error.response?.data) {
        setErrors(error.response.data);
      } else {
        setErrors({ submit: 'Failed to submit form. Please try again.' });
      }
      setResult("Error submitting form. Please check the errors above.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      fdwName: '',
      fdwWp: '',
      employerName: '',
      employerNric: '',
      agreement: '',
      compAmount: '',
      fdwSignature: '',
      fdwDate: '',
      employerSignature: '',
      employerDate: '',
      variation: '',
      restInLieu: '',
      restForgone: '',
      compensation: '',
      restWorked: '',
      fdwSignatureVar: '',
      fdwDateVar: '',
      employerSignatureVar: '',
      employerDateVar: ''
    });
    setResult('');
  };

  const handleDelete = async () => {
    if (!booking_id) {
      alert('No booking ID provided for deletion');
      return;
    }

    const confirmDelete = window.confirm(
      'Are you sure you want to delete this FDW Rest Day Agreement record? This action cannot be undone.'
    );

    if (!confirmDelete) {
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('Deleting FDW Rest Day Agreement for booking_id:', booking_id);
      const response = await api.delete(`external-forms/fdw-rest-day-agreements/${booking_id}/`);
      console.log('FDW Rest Day Agreement deleted successfully:', response.data);
      
      // Show success message
      alert('FDW Rest Day Agreement record deleted successfully!');
      
      // Reset the form after deletion
      resetForm();
      
    } catch (error) {
      console.error('Error deleting FDW Rest Day Agreement:', error);
      alert('FDW Rest Day Agreement record deleted successfully!');
    } finally {
      setIsSubmitting(false);
    }
  };

  const { user } = useAuth();
  const handleDownloadPDF = async () => {
    setIsSubmitting(true);

    try {
      console.log('Generating FDW Rest Day Agreement PDF for booking_id:', booking_id);
      
      // Prepare the data for PDF generation
      const pdfData = {
        ...formData,
        booking_id: booking_id
      };

      // Generate PDF using React PDF
      const blob = await pdf(
        <ExportPDFFdwRestDayAgreement
          user={user}
          data={pdfData}
          agencyInfo={agencyInfo}
        />
      ).toBlob();
      
      // Create blob link to download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with current date
      const today = new Date().toISOString().split('T')[0];
      const filename = booking_id 
        ? `FDW_Rest_Day_Agreement_${booking_id}_${today}.pdf`
        : `FDW_Rest_Day_Agreement_${today}.pdf`;
      link.setAttribute('download', filename);
      
      // Append to html link element page
      document.body.appendChild(link);
      
      // Start download
      link.click();
      
      // Clean up and remove the link
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('FDW Rest Day Agreement PDF generated and downloaded successfully');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="page-wrapper" style={{ marginLeft: '0' }}>
        <div className="content container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="content-page-header">
                    <h5>FDW Rest Day Agreement</h5>
                  </div>
                  <p className="text-muted small mb-3">Converted from your Word document. (Source: FDW Rest Day Agreement.docx)</p>

                  {isLoading && (
                    <div className="d-flex justify-content-center align-items-center py-5">
                      <div className="spinner-border text-primary me-3" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <span className="text-muted">Loading rest day agreement data...</span>
                    </div>
                   
                  )}

                  {errors.fetch && (
                    <div className="alert alert-danger">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      {errors.fetch}
                    </div>
                  )}

                  {errors.submit && (
                    <div className="alert alert-danger">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      {errors.submit}
                    </div>
                  )}

                  <form id="restDayForm">
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">FDW Information</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>FDW Name</label>
                              <input 
                                type="text" 
                                name="fdwName"
                                value={formData.fdwName}
                                onChange={handleInputChange}
                                className={`form-control ${errors.fdw_name ? 'is-invalid' : ''}`}
                              />
                              {errors.fdw_name && (
                                <div className="invalid-feedback">
                                  {errors.fdw_name}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Work Permit No</label>
                              <input 
                                type="text" 
                                name="fdwWp"
                                value={formData.fdwWp}
                                onChange={handleInputChange}
                                className={`form-control ${errors.fdw_wp ? 'is-invalid' : ''}`}
                              />
                              {errors.fdw_wp && (
                                <div className="invalid-feedback">
                                  {errors.fdw_wp}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Employer Information</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Employer Name</label>
                              <input 
                                type="text" 
                                name="employerName"
                                value={formData.employerName}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>NRIC/FIN</label>
                              <input 
                                type="text" 
                                name="employerNric"
                                value={formData.employerNric}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Rest Day Agreement</h6>
                      </div>
                      <div className="card-body">
                        <div className="form-check mb-3">
                          <input
                            type="radio"
                            name="agreement"
                            value="forgo"
                            checked={formData.agreement === 'forgo'}
                            onChange={handleInputChange}
                            className="form-check-input"
                            id="agreement-forgo"
                          />
                          <label className="form-check-label" htmlFor="agreement-forgo">
                            I agree to forgo my rest day and be compensated
                          </label>
                        </div>
                        
                        <div className="form-check mb-3">
                          <input
                            type="radio"
                            name="agreement"
                            value="work"
                            checked={formData.agreement === 'work'}
                            onChange={handleInputChange}
                            className="form-check-input"
                            id="agreement-work"
                          />
                          <label className="form-check-label" htmlFor="agreement-work">
                            I agree to work on my rest day and be compensated
                          </label>
                        </div>

                        <div className="input-block mb-3">
                          <label>Compensation Amount (S$)</label>
                          <input 
                            type="number" 
                            name="compAmount"
                            value={formData.compAmount}
                            onChange={handleInputChange}
                            className="form-control"
                            min="0"
                            step="0.01"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Signatures - Initial Agreement</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>FDW Signature</label>
                              <input 
                                type="text" 
                                name="fdwSignature"
                                value={formData.fdwSignature}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Date</label>
                              <input 
                                type="date" 
                                name="fdwDate"
                                value={formData.fdwDate}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Employer Signature</label>
                              <input 
                                type="text" 
                                name="employerSignature"
                                value={formData.employerSignature}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Date</label>
                              <input 
                                type="date" 
                                name="employerDate"
                                value={formData.employerDate}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Variation of Agreement</h6>
                      </div>
                      <div className="card-body">
                        <div className="input-block mb-3">
                          <label>Variation Details</label>
                          <textarea 
                            name="variation"
                            value={formData.variation}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="3"
                          />
                        </div>

                        <div className="row">
                          <div className="col-lg-4 col-12">
                            <div className="form-check mb-2">
                              <input
                                type="checkbox"
                                name="restInLieu"
                                checked={formData.restInLieu}
                                onChange={handleInputChange}
                                className="form-check-input"
                                id="restInLieu"
                              />
                              <label className="form-check-label" htmlFor="restInLieu">
                                Rest day in lieu
                              </label>
                            </div>
                          </div>
                          <div className="col-lg-4 col-12">
                            <div className="form-check mb-2">
                              <input
                                type="checkbox"
                                name="restForgone"
                                checked={formData.restForgone}
                                onChange={handleInputChange}
                                className="form-check-input"
                                id="restForgone"
                              />
                              <label className="form-check-label" htmlFor="restForgone">
                                Rest day forgone
                              </label>
                            </div>
                          </div>
                          <div className="col-lg-4 col-12">
                            <div className="form-check mb-2">
                              <input
                                type="checkbox"
                                name="compensation"
                                checked={formData.compensation}
                                onChange={handleInputChange}
                                className="form-check-input"
                                id="compensation"
                              />
                              <label className="form-check-label" htmlFor="compensation">
                                Compensation paid
                              </label>
                            </div>
                          </div>
                        </div>

                        <div className="input-block mb-3">
                          <label>Rest Day Worked Details</label>
                          <input 
                            type="text" 
                            name="restWorked"
                            value={formData.restWorked}
                            onChange={handleInputChange}
                            className="form-control"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Signatures - Variation</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>FDW Signature</label>
                              <input 
                                type="text" 
                                name="fdwSignatureVar"
                                value={formData.fdwSignatureVar}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Date</label>
                              <input 
                                type="date" 
                                name="fdwDateVar"
                                value={formData.fdwDateVar}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Employer Signature</label>
                              <input 
                                type="text" 
                                name="employerSignatureVar"
                                value={formData.employerSignatureVar}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Date</label>
                              <input 
                                type="date" 
                                name="employerDateVar"
                                value={formData.employerDateVar}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12">
                        <div className="btn-path text-end">
                          <button type="button" className="btn btn-cancel bg-primary-light me-3" onClick={resetForm} disabled={isSubmitting}>
                            Reset
                          </button>
                          <button
                            type="button"
                            onClick={handleDelete}
                            className="btn btn-danger me-3"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? 'Deleting...' : 'Clean'}
                          </button>
                          <button
                            type="button"
                            onClick={handleDownloadPDF}
                            className="btn btn-success me-3"
                            disabled={isSubmitting}
                            title="Download PDF of current form data"
                          >
                            {isSubmitting ? 'Generating...' : (
                              <>
                                <i className="fas fa-download me-2"></i>
                                Download PDF
                              </>
                            )}
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-primary" 
                            onClick={submitForm}
                            disabled={isSubmitting || isLoading}
                          >
                            {isSubmitting ? (
                              <>
                                <i className="fas fa-spinner fa-spin me-2"></i>
                                Submitting...
                              </>
                            ) : (
                              'Submit'
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                    <p className="text-muted small mt-3">{result}</p>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FdwRestDayAgreement;
